import * as SignalR from '@aspnet/signalr';
import { toastr } from 'react-redux-toastr';
import { setConnectionStatus } from './../../actions';
import { HOST } from '../../helpers/ajax';
import { signalR as signalRHelpers } from '../../helpers';

export const subscriptionTypes = {
  activeNodeIssues: 'activeNodeIssues',
  annotations: 'annotations',
  builds: 'builds',
  dispatchers: 'dispatchers',
  installerTestExpected: 'installerTestExpected',
  jobLabels: 'jobLabels',
  jobBuilds: 'jobBuilds',
  jobsByDispatcherType: 'jobsByDispatcherType',
  jobsByDateType: 'jobsByDateType',
  jobsRelationships: 'jobsRelationships',
  jobScenarios: 'jobScenarios',
  latestImageBaselines: 'latestImageBaselines',
  metrics: 'metrics',
  notificationSubscriptions: 'userNotificationSubscriptions',
  params: 'params',
  scenarios: 'scenarios'
};

const typeDispatcherMap = {};

export class SignalRHubManager {
  hubConnection = null;
  dispatchStatusChange = null;

  /**
   * Set tracking fully joined subscription types
   */
  subscribedTypes = new Set();
  /**
   * Set tracking subscriptions that failed to join due to offline hub
   */
  pendingSubscribe = new Set();

  _createHubConnection = async () => {
    this.hubConnection = new SignalR.HubConnectionBuilder()
      .withUrl(`${HOST}signalRHubManager`, {
        skipNegotiation: true,
        transport: SignalR.HttpTransportType.WebSockets
      })
      .configureLogging(SignalR.LogLevel.Trace)
      .build();

    this.hubConnection.onclose(() => {
      // try to reset the connection
      setTimeout(() => this._startHubConnection(), 5000);

      // after brief time after, warn user if connection not established
      setTimeout(() => {
        if (!this.hubConnection) {
          toastr.error('Live updates is disabled.');
          this.dispatchStatusChange(false);
        }
      }, 7000);
    });

    this.hubConnection.on(signalRHelpers.RECEIVE_PAYLOAD, (type, payloads) => {
      let filteredPayloads = payloads.map(p => JSON.parse(p));
      const { dispatcher, args, payloadsHandler } = typeDispatcherMap[type];
      if (payloadsHandler)
      {
        filteredPayloads = payloadsHandler(args, filteredPayloads);
      }
      dispatcher(filteredPayloads, signalRHelpers.RECEIVE_PAYLOAD);
    });

    this.hubConnection.on(signalRHelpers.DELETE_PAYLOAD, (type, payloads) => {
      let filteredPayloads = payloads.map(p => JSON.parse(p));
      const { dispatcher, args, payloadsHandler } = typeDispatcherMap[type];
      if (payloadsHandler)
      {
        filteredPayloads = payloadsHandler(args, filteredPayloads);
      }
      dispatcher(filteredPayloads, signalRHelpers.DELETE_PAYLOAD);
    });

    await this._startHubConnection();
  }

  _startHubConnection = () =>
  {
    console.log('Try to start a hub connection');
    this.hubConnection
      .start()
      .then(() => {
        this.dispatchStatusChange(true);
        // rejoin any previously subscribed or pending subscriptions
        this.joinGroups(Array.from(this.subscribedTypes));
        this.joinGroups(Array.from(this.pendingSubscribe));
        this.pendingSubscribe.clear();

        toastr.success('Live updates is enabled.');
      })
      .catch(err => toastr.error(`Failed to enable live updates: ${err}`));
  }

  subscribe(subscribeType, args, dispatcher, payloadsHandler) {
    if (!dispatcher)
    {
      console.log('dispatcher must be defined when subscribe a type');
      return;
    }
    if (!this.hubConnection)
    {
      this._createHubConnection();
    }

    typeDispatcherMap[subscribeType] =
     { dispatcher: dispatcher, args: args, payloadsHandler: payloadsHandler };

    if (this.hubConnection.state === SignalR.HubConnectionState.Connected)
    {
      this.joinGroups([subscribeType]);
    }
    else
    {
      this.pendingSubscribe.add(subscribeType);
    }
  }

  unsubscribe(subscribeTypes) {
    if (!this.hubConnection)
    {
      return;
    }
    this.leaveGroups(subscribeTypes);
  }

  clearAndStop() {
    if (!this.hubConnection)
    {
      return;
    }
    this.unsubscribe(this.subscribedTypes);
    this.subscribedTypes.clear();
    this.hubConnection.stop();
  }

  joinGroups(subscribeTypes) {
    // only join types that are not already subcribed to
    const typesToJoin = subscribeTypes.filter(type => !this.subscribedTypes.has(type));

    this.hubConnection.invoke('JoinGroups', typesToJoin)
      .catch(err => {
        console.log(`Error in SignalRHubManager JoinGroups: ${err}`);
        return;
      });

    typesToJoin.forEach(type => {
      this.subscribedTypes.add(type);
    });
  }

  leaveGroups(subscribeTypes) {
    this.hubConnection.invoke('LeaveGroups', subscribeTypes).catch(err =>
    {
      console.log(`Error while unsubscribing subscribeType: ${err}`);
      return;
    });

    subscribeTypes.forEach(type =>
      this.subscribedTypes.delete(type));
  }

  hasDispatcher() {
    return this.hubConnection && this.dispatchStatusChange;
  }

  setDispatcher(dispatch) {
    this.dispatchStatusChange = (connected) => {
      dispatch(setConnectionStatus(connected));
    };
  }

  getSubscribedTypes() {
    return this.subscribedTypes;
  }
}

export const signalRHubManager = new SignalRHubManager();


// WEBPACK FOOTER //
// src/actions/signalRHub/signalRHubManager.js